import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';

import { RankRewardTitle } from 'components/leaderboard/CumulativeRankRewards/RankRewardTitle';

import type { SpecialRoomReward_roomsConfig } from './__generated__/index.graphql';
import bg from './assets/bg.webp';
import icon from './assets/icon.webp';

const EntryFeeContainer = styled(Vertical)`
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: 16px;
  border: 1px solid var(--c-nd-50);
  background:
    linear-gradient(0deg, #101010 0%, rgba(16, 16, 16, 0) 100%) padding-box,
    url(${bg}) center/cover padding-box,
    var(--c-gradient-gold) border-box;
  backdrop-filter: blur(16px);
  overflow: hidden;

  & > * {
    width: 100%;
  }
`;

const StyledHorizontal = styled(Horizontal)`
  justify-content: space-around;

  & > * {
    width: 100%;
  }
`;

export const SpecialRoomReward = ({
  roomsConfig,
}: {
  roomsConfig: SpecialRoomReward_roomsConfig;
}) => {
  const {
    boostedRewardsRoomsName,
    roomsWithBoostedRewardsCount,
    roomsWithBoostedRewardsBasisPoint,
    boostedRewardsRoomsSo5RewardConfigs,
  } = roomsConfig;

  if (!boostedRewardsRoomsSo5RewardConfigs) {
    return null;
  }

  return (
    <EntryFeeContainer gap={2}>
      <Horizontal>
        <img src={icon} width={36} height={36} alt="Golden Arena" />
        <Vertical gap={0.5}>
          <LabelL bold>
            {boostedRewardsRoomsName || (
              <FormattedMessage
                id="SpecialRoomReward.goldenArena"
                defaultMessage="Golden Arena"
              />
            )}
          </LabelL>
          <LabelS color="var(--c-nd-600)">
            {!!roomsWithBoostedRewardsCount && (
              <FormattedMessage
                id="SpecialRoomReward.rewardForNumberGoldenArenas"
                defaultMessage="{count, number} Arenas"
                values={{
                  count: roomsWithBoostedRewardsCount,
                }}
              />
            )}
            {!!roomsWithBoostedRewardsBasisPoint &&
              !roomsWithBoostedRewardsCount && (
                <FormattedMessage
                  id="SpecialRoomReward.percentageOfGoldenArenas"
                  defaultMessage="{value, number, percent} chance for each Arena"
                  values={{
                    value: roomsWithBoostedRewardsBasisPoint / 10000,
                  }}
                />
              )}
          </LabelS>
        </Vertical>
      </Horizontal>
      <StyledHorizontal>
        <Scrollable itemToDisplay={3} overhang={5}>
          {boostedRewardsRoomsSo5RewardConfigs.map(
            ({ fromRank, toRank, rewardConfigs }) => (
              <Vertical key={fromRank} gap={1.5} center>
                <Horizontal>
                  {rewardConfigs.map(rewardConfig => (
                    <AnyRewardConfig
                      key={rewardConfig.id}
                      anyRewardConfig={rewardConfig}
                    />
                  ))}
                </Horizontal>
                <Vertical gap={0.5}>
                  <RankRewardTitle startRank={fromRank} endRank={toRank} />
                </Vertical>
              </Vertical>
            )
          )}
        </Scrollable>
      </StyledHorizontal>
    </EntryFeeContainer>
  );
};

SpecialRoomReward.fragments = {
  roomsConfig: gql`
    fragment SpecialRoomReward_roomsConfig on RoomsConfig {
      slug
      boostedRewardsRoomsName
      boostedRewardsRoomsSo5RewardConfigs {
        id
        fromRank
        toRank
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
      roomsWithBoostedRewardsCount
      roomsWithBoostedRewardsBasisPoint
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<SpecialRoomReward_roomsConfig>,
};
